import { UseFormReturnType } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { useState, useEffect } from "react";
import TextInput from "src/components/TextInput/TextInput";
import { Loader, ThemeIcon } from "@mantine/core";
import {
  useGetIsUsernameAvailableLazyQuery,
  useGetSuggestedUserNameLazyQuery,
} from "src/generated/graphql";
import { AiFillCheckCircle } from "react-icons/ai";

type IValues = {
  username: string;
};

export default function useUserName<T extends IValues>(
  form: UseFormReturnType<T, (values: T) => T>
) {
  const [usernameDebounced] = useDebouncedValue(form.values.username, 500);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState<
    boolean | undefined
  >(undefined);

  useEffect(() => {
    checkUsernameAvailability(form.values.username || "");
  }, [usernameDebounced]);

  const [getUserName, { loading: isUsernameLoading }] =
    useGetSuggestedUserNameLazyQuery({
      fetchPolicy: "network-only",
      onCompleted(data) {
        form.setValues({
          ...form.values,
          username: data?.suggestedUserName,
        });
      },
    });

  const [checkUsername, { loading: isUsernameChecking }] =
    useGetIsUsernameAvailableLazyQuery();

  const checkUsernameAvailability = async (username: string) => {
    if (username.length > 3 && username.length < 31) {
      await checkUsername({
        variables: {
          username,
        },
        onCompleted: (resp) => {
          setIsUsernameAvailable(resp.checkUsernameAvailability);
        },
        fetchPolicy: "no-cache",
      });
    } else {
      setIsUsernameAvailable(undefined);
    }
  };

  const FieldUserName = () => (
    <TextInput
      withAsterisk
      maxLength={30}
      label="Username"
      rightSection={
        isUsernameLoading || isUsernameChecking ? (
          <Loader size="xs" />
        ) : !!isUsernameAvailable ? (
          <ThemeIcon color="green" size={18} radius="xl">
            <AiFillCheckCircle />
          </ThemeIcon>
        ) : undefined
      }
      {...form.getInputProps("username")}
      error={
        isUsernameAvailable === false
          ? "Username is taken!"
          : form.errors["username"]
      }
    />
  );

  return {
    isUsernameChecking,
    isUsernameLoading,
    isUsernameAvailable,
    usernameDebounced,
    checkUsernameAvailability,
    getUserName,
    FieldUserName,
  };
}
